import { Component, OnInit, ChangeDetectorRef, ViewChild, HostListener, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { OrderSummariesComponent } from '../../utils/index';
import {
  InspectionSectionService,
  InspectionPointService,
  InspectionFolderService,
  SignalRService,
  MultiEditService,
  SessionDetectService
} from '../../shared/services/index';
import { Inspection, InspectionSection, InspectionPoint, InspectionFolder, InspectionSession } from '../../shared/models/index';
import { BasePageComponent } from '../../base-page.component';
import { ConfirmComponent } from '../../modal/confirm.component';
import { ConfirmWithOptionsComponent } from '../../modal/confirm-with-options.component';
import * as _ from 'lodash';
import { Subject, Subscription, forkJoin, of } from 'rxjs';
import { SectionMenuComponent } from '../../menus/section.menu';
import { SnackBarService } from '../../shared/services/snack-bar.service';
import { SectionClipboardService } from '../../shared/services/section-clipboard.service';

@Component({
  templateUrl: './detail.component.html',
  standalone: false
})
export class SectionDetailPageComponent extends BasePageComponent implements OnInit, OnDestroy {
  section: InspectionSection;
  folder: InspectionFolder;
  inspectionId: string;
  sectionId: string;
  folderId: string;
  inspection: Inspection;
  points: any[] = [];
  folders: any[] = [];
  itemsCount = 0;
  title = '';
  completedPointsCount = 0;
  updateSubject = new Subject<any>();
  selectedItems: { id: string; isFolder: boolean; name: string }[] = [];
  loading = false;
  multiEditSubscription: Subscription;
  @ViewChild('orderPoints', { static: true }) orderPointsModal: OrderSummariesComponent;
  @ViewChild('deleteConfirm') deleteConfirm: ConfirmComponent;
  @ViewChild('pointsDeleteConfirm', { static: true }) pointsDeleteConfirm: ConfirmComponent;
  @ViewChild('deleteFoldersConfirm', { static: true }) deleteFoldersConfirm: ConfirmComponent;
  @ViewChild('selectedDeleteConfirm', { static: true }) selectedDeleteConfirm: ConfirmComponent;
  @ViewChild('pastingFolderToFolderConfirm', { static: true }) pastingFolderToFolderConfirm: ConfirmComponent;
  @ViewChild('clipboardPasteReplaceConfirm', { static: true })
  clipboardPasteReplaceConfirm: ConfirmWithOptionsComponent;
  constructor(
    location: Location,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private sectionService: InspectionSectionService,
    private pointService: InspectionPointService,
    private folderService: InspectionFolderService,
    public snackBar: SnackBarService,
    private clipboard: SectionClipboardService,
    private multiEditService: MultiEditService,
    private sessionDetect: SessionDetectService,
    private cdRef: ChangeDetectorRef
  ) {
    super(location);
  }

  ngOnInit(): void {
    this.initRoute();
  }

  initRoute(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      const id = params['inspectionId'];
      if (id == null) {
        this.router.navigate([this.inspectionsRoute]);
      }

      this.inspectionId = id;

      const sectionId = params['sectionId'];
      if (sectionId == null) {
        this.router.navigate([this.inspectionsRoute, this.inspectionId]);
      }

      this.sectionId = sectionId;

      this.folderId = params['folderId'];

      this.initData();
      this.multiEditSubscription = this.multiEditService.sectionUpdate(this.sectionId).subscribe((sectionId) => {
        this.initData();
        this.updateSubject.next(sectionId);
      });
    });
  }

  openPoint(point: InspectionPoint): void {
    this.router.navigate([this.inspectionsRoute, point.inspection.id, 'points', point.id]);
  }

  openFolder(folder: InspectionFolder): void {
    this.router.navigate([this.inspectionsRoute, folder.inspection.id, 'sections', this.sectionId, 'folders', folder.id]);
  }

  selectPoint(point: any) {
    this.updateSelection(point);
    if (point.selected) {
      this.selectedItems.push({ id: point.id, isFolder: false, name: point.name });
    } else {
      this.selectedItems = this.selectedItems.filter((s) => s.id !== point.id);
    }
  }

  updateSelection(item: any) {
    item.selected = !item.selected;
  }

  selectFolder(folder: any) {
    this.updateSelection(folder);
    if (folder.selected) {
      this.selectedItems.push({ id: folder.id, isFolder: true, name: folder.name });
    } else {
      this.selectedItems = this.selectedItems.filter((s) => s.id !== folder.id);
    }
  }

  initData(): void {
    this.sectionService.getSection(this.inspectionId, this.sectionId).subscribe((el) => {
      this.section = el;
      if (!this.folderId) {
        this.points = this.section.points ? _.filter(this.section.points, (p: InspectionPoint) => this.isRootPoint(p)) : [];
        this.folders = this.section.folders ? this.section.folders : [];
        this.itemsCount = this.points.length + this.folders.length;
        this.title = this.section.name;
        this.inspection = this.section.inspection;
      } else {
        this.folder = this.section.inspection.foldersById[this.folderId];
        this.points = this.folder.points ? this.folder.points : [];
        this.folders = this.folder.folders ? this.folder.folders : [];
        this.itemsCount = this.points.length + this.folders.length;
        this.title = this.folder.name;
        this.inspection = this.folder.inspection;
      }

      this.selectedItems.push(...this.points.filter((p) => p.selected).map((p) => ({ id: p.id, isFolder: false, name: p.name })));
      this.selectedItems.push(...this.folders.filter((p) => p.selected).map((p) => ({ id: p.id, isFolder: true, name: p.name })));
      this.calculateCompletedItems();
      this.cdRef.detectChanges();
    });
  }

  isRootPoint(p: InspectionPoint) {
    if (!p.folderId || p.folderId === 'undefined') return true;
    if (!this.section.inspection.foldersById[p.folderId]) return true;
    return false;
  }

  public calculateCompletedItems() {
    this.completedPointsCount = _.sumBy(this.points, (el) => (el.status === 100 ? 1 : 0));
    for (let i = 0; i < this.folders.length; i++) {
      const folder = this.folders[i];
      const completedPoints = _.sumBy(folder.points, (p: any) => (p.status === 100 ? 1 : 0));
      if (completedPoints === folder.points.length) {
        this.completedPointsCount++;
        folder.status = 100;
      }
    }
  }

  public openMenu(): void {
    this.modal.modalFooter = false;
    this.modal.modalHeader = false;
    this.modal.modalMessage = false;
    this.modal.open(SectionMenuComponent, {
      anyFolders: this.selectedItems.filter((s) => s.isFolder).length > 0,
      anyPoints: this.selectedItems.filter((s) => !s.isFolder).length > 0,
      showFoldersMenu: !this.folderId,
      inspection: this.inspection,
      isTemplate: this.inspection.isTemplate()
    });
  }

  @HostListener('window:keydown', ['$event'])
  onKeyPress($event: KeyboardEvent) {
    if (($event.ctrlKey || $event.metaKey) && $event.keyCode === 67) {
      if (this.clipboard.canProcess(this.inspection)) {
        this.copy();
      }
    }
    if (($event.ctrlKey || $event.metaKey) && $event.keyCode === 88) {
      if (this.clipboard.canProcess(this.inspection)) {
        this.cut();
      }
    }
    if (($event.ctrlKey || $event.metaKey) && $event.keyCode === 86) {
      if (this.clipboard.canProcess(this.inspection)) {
        this.openPastePartiallyIfNeedConfirm();
      }
    }
  }

  public selectMenu(item: any) {
    switch (item) {
      case 1:
        this.gotoEditPage();
        break;
      case 2:
        this.importPoint();
        break;
      case 3:
        this.createPoint();
        break;
      case 4:
        this.createFolder();
        break;
      case 5:
        this.openDeleteFoldersConfirm();
        break;
      case 6:
        this.cut();
        break;
      case 7:
        this.openPastePartiallyIfNeedConfirm();
        break;
      case 8:
        this.orderItems();
        break;
      case 9:
        this.openDeleteConfirm();
        break;
      case 10:
        this.openPointsDeleteConfirm();
        break;
      case 11:
        this.makePointsDone();
        break;
      case 12:
        this.gotoEditJsonPage();
        break;
      case 13:
        this.copy();
        break;
      case 14:
        this.openSelectedDeleteConfirm();
        break;
      default:
    }
  }

  public makePointsDone() {
    if (this.selectedItems.length > 0) {
      const points = this.section.points.filter((p) => this.selectedItems.some((i) => i.id === p.id || i.id === p.folderId));
      const ids = points.filter((p) => p.status !== InspectionPoint.Done).map((p) => p.id);
      this.pointService.updatePointsStatus(this.inspectionId, ids, InspectionPoint.Done).subscribe((data) => {
        this.removeSelection();
      });
    }
  }

  public gotoEditPage() {
    if (this.folderId) {
      return this.router.navigate([
        this.inspectionsRoute,
        this.section.inspection.id,
        'sections',
        this.section.id,
        'folders',
        this.folderId,
        'edit'
      ]);
    } else {
      return this.router.navigate([this.inspectionsRoute, this.section.inspection.id, 'sections', this.section.id, 'edit']);
    }
  }

  public createPoint() {
    if (this.folderId) {
      return this.router.navigate([
        this.inspectionsRoute,
        this.section.inspection.id,
        'sections',
        this.section.id,
        'folders',
        this.folderId,
        'points',
        'new'
      ]);
    }
    return this.router.navigate([this.inspectionsRoute, this.section.inspection.id, 'sections', this.section.id, 'points', 'new']);
  }

  public importPoint() {
    if (this.folderId) {
      return this.router.navigate([
        this.inspectionsRoute,
        this.section.inspection.id,
        'sections',
        this.section.id,
        'folders',
        this.folderId,
        'addpoint'
      ]);
    } else {
      return this.router.navigate([this.inspectionsRoute, this.section.inspection.id, 'sections', this.section.id, 'addpoint']);
    }
  }

  public createFolder() {
    return this.router.navigate([this.inspectionsRoute, this.section.inspection.id, 'sections', this.section.id, 'folder', 'new']);
  }

  public gotoEditJsonPage() {
    return this.router.navigate([this.inspectionsRoute, this.section.inspection.id, 'sections', this.section.id, 'editjson']);
  }

  public deleteSelectedItems(e: any) {
    const pointIds = this.selectedItems.filter((s) => !s.isFolder).map((p) => p.id);
    const folderIds = this.getSelectedItems(this.folders);

    return forkJoin({
      folders: folderIds.length ? this.folderService.deleteFolders(this.section.inspection.id, this.sectionId, folderIds) : of([]),
      points: pointIds.length ? this.pointService.deletePoints(this.inspectionId, pointIds) : of([])
    }).subscribe(({ folders, points }) => {
      this.updateSubject.next(folders);
      this.initData();
      this.points.forEach((p) => (p.selected = false));
      this.folders.forEach((p) => (p.selected = false));
      this.selectedItems = [];
      this.updateSubject.next(points);
    });
  }

  public deleteFolders(e: any) {
    this.folderService.deleteFolders(this.section.inspection.id, this.sectionId, this.getSelectedItems(this.folders)).subscribe((el) => {
      this.section = el;
      this.selectedItems = this.selectedItems.filter((s) => !s.isFolder);
      this.initData();
      this.updateSubject.next(el);
    });
  }

  private getSelectedItems(items: any[]) {
    const selectedFolders: string[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].selected) {
        selectedFolders.push(items[i].id);
      }
    }
    return selectedFolders;
  }

  public cut() {
    if (this.selectedItems.length > 0) {
      this.snackBar.success('snack-bar.menu.added-to-clipboard');
    } else {
      this.snackBar.warn('snack-bar.menu.nothing-selected');
    }
    const itemsToCut: any = {};
    itemsToCut.sourceInspectionId = this.inspectionId;
    itemsToCut.sourceSectionId = this.sectionId;
    itemsToCut.selectedItems = _.uniqBy(this.selectedItems, 'id');
    itemsToCut.deleteSource = true;
    this.clipboard.setData(itemsToCut, this.inspection);
  }

  public copy() {
    if (this.selectedItems.length > 0) {
      this.snackBar.success('snack-bar.menu.added-to-clipboard');
    } else {
      this.snackBar.warn('snack-bar.menu.nothing-selected');
    }
    const itemsToCut: any = {};
    itemsToCut.sourceInspectionId = this.inspectionId;
    itemsToCut.sourceSectionId = this.sectionId;
    itemsToCut.selectedItems = _.uniqBy(this.selectedItems, 'id');
    itemsToCut.deleteSource = false;
    this.clipboard.setData(itemsToCut, this.inspection);
  }

  public replaceOnPast(e: any) {
    this.paste(e);
  }

  public openPastePartiallyIfNeedConfirm() {
    if (this.folderId) {
      const clipboard: any = this.clipboard.getData(this.inspection);
      if (clipboard.selectedItems.some((s) => s.isFolder)) {
        this.pastingFolderToFolderConfirm.open();
        return;
      }
    }
    this.paste();
  }

  public paste(replace?: boolean) {
    const clipboard: any = this.clipboard.getData(this.inspection);
    if (clipboard && clipboard.selectedItems && clipboard.selectedItems.length > 0) {
      const pointsNames: Array<string> = clipboard.selectedItems.filter((s) => !s.isFolder).map((p) => p.name);
      const foldersNames: Array<string> = clipboard.selectedItems.filter((s) => s.isFolder).map((p) => p.name);

      const intersectedPointsItems = this.points.filter((value) => pointsNames.includes(value.name));
      // const intersectedFoldersItems = this.folders.filter((value) => foldersNames.includes(value.name));
      if (replace === undefined && !!intersectedPointsItems.length) {
        this.clipboardPasteReplaceConfirm.open();
        return;
      }
      this.snackBar.success('snack-bar.menu.pasting');
      // todo change

      if (this.sectionId) {
        this.clipboard.clearData(this.inspection);
        this.pointService
          .moveToSection(this.inspectionId, this.sectionId, this.folderId, clipboard)
          .flatMap((result) => {
            if (replace && !!intersectedPointsItems.length) {
              return this.pointService.deletePoints(
                this.inspectionId,
                intersectedPointsItems.map((x) => x.id)
              );
            }
            return of(result);
          })
          .subscribe(
            (el) => {
              this.initData();
              this.points.forEach((p) => (p.selected = false));
              this.updateSubject.next(el);
            },
            (err) => {
              this.snackBar.warn('snack-bar.something-went-wrong');
            }
          );
      }
    } else {
      this.snackBar.warn('snack-bar.menu.clipboard-is-empty');
    }
  }

  public removeSelection() {
    _.forEach(this.points, (item: any) => {
      item.selected = false;
    });
    _.forEach(this.folders, (item: any) => {
      item.selected = false;
    });
  }

  public orderItems() {
    let items = _.map(this.points, (el: InspectionPoint) => ({
      id: el.id,
      name: el.name,
      type: 'point',
      order: el.order
    }));

    const folders = _.map(this.folders, (f: InspectionFolder) => ({
      id: f.id,
      name: f.name,
      type: 'folder',
      order: f.order
    }));
    items = _.concat(items, folders);

    this.orderPointsModal.open(items);
  }

  saveItemsOrder(items: any) {
    const orderableItems = _.map(items, (item: any) => ({
      id: item.id,
      order: item.order,
      type: item.type === 'folder' ? 100 : 200
    }));

    this.sectionService.orderItems(this.inspectionId, this.sectionId, orderableItems).subscribe((el) => {});
  }

  openDeleteConfirm() {
    this.deleteConfirm.open();
  }

  deleteSection(e: any) {
    this.sectionService.deleteSection(this.inspectionId, this.sectionId).subscribe((success: boolean) => {
      if (success) {
        this.goToBack();
      }
    });
  }

  deleteFolder(e: any) {
    const folders = [this.folderId];
    this.folderService.deleteFolders(this.inspectionId, this.sectionId, folders).subscribe((section: InspectionSection) => {
      if (section) {
        this.goToBack();
      }
    });
  }

  openPointsDeleteConfirm() {
    if (this.selectedItems.filter((s) => !s.isFolder).length > 0) {
      this.pointsDeleteConfirm.open();
    }
  }

  openSelectedDeleteConfirm() {
    if (this.selectedItems.length > 0) {
      this.selectedDeleteConfirm.open();
    }
  }

  openDeleteFoldersConfirm() {
    if (this.selectedItems.filter((s) => s.isFolder).length > 0) {
      this.deleteFoldersConfirm.open();
    }
  }

  deletePoints(e: any) {
    const pointIds = this.selectedItems.filter((s) => !s.isFolder).map((p) => p.id);
    this.pointService.deletePoints(this.inspectionId, pointIds).subscribe((deletedPointIds) => {
      if (deletedPointIds) {
        this.selectedItems = this.selectedItems.filter((s) => s.isFolder);
        this.initData();
        this.updateSubject.next(deletedPointIds);
      }
    });
  }

  navigateToSession(session: InspectionSession) {
    this.sessionDetect.navigate(session);
  }

  cancel() {}

  ngOnDestroy() {
    this.multiEditSubscription?.unsubscribe();
  }
}
